import moment from 'moment-timezone';
import { DEFAULT_TIMEZONE } from '../../utils/date-time';
import 'moment/locale/pt-br';
import { MOVEMENT_WARNING_LABELS, formatQuantity } from '../estoque/units';

export type PeriodKey = 'today' | '7d' | 'month' | 'last-month' | 'all' | 'custom';

export type SalesFilters = {
    period: PeriodKey;
    customStart: string;
    customEnd: string;
    clientId: string;
};

export const PERIOD_LABELS: Record<PeriodKey, string> = {
    today: 'Hoje',
    '7d': 'Últimos 7 dias',
    month: 'Este mês',
    'last-month': 'Mês passado',
    all: 'Todo o período',
    custom: 'Personalizado',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
    PAID: 'Pago',
    PENDING: 'A receber',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
    CASH: 'Dinheiro',
    PIX: 'Pix',
    DEBIT: 'Débito',
    CREDIT: 'Crédito',
    TRANSFER: 'Transferência',
    OTHER: 'Outro',
};

const LAST_PAYMENT_METHOD_KEY = 'salonflow:last-payment-method';

export const readLastPaymentMethod = () => {
    try {
        const stored = localStorage.getItem(LAST_PAYMENT_METHOD_KEY);
        return stored && PAYMENT_METHOD_LABELS[stored] ? stored : 'PIX';
    } catch {
        return 'PIX';
    }
};

export const saveLastPaymentMethod = (method: string) => {
    try {
        localStorage.setItem(LAST_PAYMENT_METHOD_KEY, method);
    } catch {
        // A preferencia e apenas uma conveniencia; sem ela, a tela volta a sugerir Pix.
    }
};

// Limites no dia local: a data pura seria lida como meia-noite UTC pela API e cortaria o ultimo dia.
export const getPeriodRange = ({ period, customStart, customEnd }: SalesFilters, timezone = DEFAULT_TIMEZONE) => {
    const now = moment().tz(timezone);
    const toRange = (start?: moment.Moment, end?: moment.Moment) => ({
        startAt: start?.startOf('day').toISOString(),
        endAt: end?.endOf('day').toISOString(),
    });

    switch (period) {
        case 'today':
            return toRange(now.clone(), now.clone());
        case '7d':
            return toRange(now.clone().subtract(6, 'days'), now.clone());
        case 'month':
            return toRange(now.clone().startOf('month'), now.clone().endOf('month'));
        case 'last-month': {
            const last = now.clone().subtract(1, 'month');
            return toRange(last.clone().startOf('month'), last.clone().endOf('month'));
        }
        case 'custom':
            return toRange(customStart ? moment.tz(customStart, 'YYYY-MM-DD', true, timezone) : undefined, customEnd ? moment.tz(customEnd, 'YYYY-MM-DD', true, timezone) : undefined);
        default:
            return toRange();
    }
};

export const formatDayLabel = (value: string | Date, timezone = DEFAULT_TIMEZONE) => {
    const date = moment(value).tz(timezone);
    const now = moment().tz(timezone);
    if (date.isSame(now, 'day')) return 'Hoje';
    if (date.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Ontem';

    const label = date.format(date.isSame(now, 'year') ? 'dddd, D [de] MMMM' : 'D [de] MMMM [de] YYYY');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

export const formatTime = (value: string | Date, timezone = DEFAULT_TIMEZONE) => moment(value).tz(timezone).format('HH:mm');

export const formatDateTime = (value?: string | Date, timezone = DEFAULT_TIMEZONE) =>
    value ? moment(value).tz(timezone).format('DD/MM/YYYY [às] HH:mm') : '-';

export const describeItems = (items: any[] = []) =>
    items.map((item) => `${item?.name || 'Item'} × ${formatQuantity(item?.quantity)}`).join(', ');

export const getDiscountRuleLabel = (type?: string, value?: number) => {
    if (type === 'PERCENTUAL' && Number(value)) return `${Number(value)}%`;
    return '';
};

// Mesma regra de `calculateTotals` da API, para o resumo bater com o que sera gravado.
export const calculateTotals = (subTotal: number, discountType: string, discountValue: number) => {
    let discountAmount = 0;
    if (discountType === 'PERCENTUAL') discountAmount = (subTotal * Math.min(100, Math.max(0, discountValue))) / 100;
    if (discountType === 'VALOR') discountAmount = Math.min(subTotal, Math.max(0, discountValue));

    return { subTotal, discountAmount, total: Math.max(0, subTotal - discountAmount) };
};

export const formatWarning = (warning: string) => {
    const code = warning.replace(/^REVERSAO:/, '');
    return MOVEMENT_WARNING_LABELS[code] || code;
};

// Saldo insuficiente traz o detalhe por item; os demais erros so a mensagem.
export const getApiErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    const details: any[] = Array.isArray(data?.details) ? data.details : [];
    const shortages = details.filter((detail) => detail?.name);

    if (shortages.length) {
        const list = shortages
            .map((d) => `${d.name} (disponível ${formatQuantity(d.onHand)}, pedido ${formatQuantity(d.requested)})`)
            .join('; ');
        return `Saldo insuficiente: ${list}.`;
    }

    return data?.message || fallback;
};
